import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FadeImage } from '../motion/FadeImage';

const PROJECTS = [
    {
        id: 'p01',
        title: 'Stunning Entry Foyer',
        meta: null,
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/nv30hvci_IMG_5198.webp',
        span: 'md:col-span-7',
        clip: 'clip-notch-tr',
    },
    {
        id: 'p02',
        title: 'La Cueva Modern',
        meta: 'Santa Fe',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/zwz7g41m_IMG_0465.webp',
        span: 'md:col-span-5',
        clip: 'clip-notch-bl',
    },
    {
        id: 'p03',
        title: 'Coffered Ceiling',
        meta: 'Detail trim · black-and-white contrast',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/qqhksmma_IMG_5072.webp',
        span: 'md:col-span-5',
        clip: 'clip-arch',
    },
    {
        id: 'p04',
        title: 'Vermillion Stair',
        meta: 'Custom crimson stairwell finish',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/02c9ui6a_IMG_5047.webp',
        span: 'md:col-span-7',
        clip: 'clip-notch-tr',
    },
    {
        id: 'p05',
        title: 'Placitas Adobe',
        meta: 'Interior stucco · fireplace refinish',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/637tla2u_IMG_0816.webp',
        span: 'md:col-span-4',
        clip: 'clip-notch-bl',
    },
    {
        id: 'p06',
        title: 'Sage Chamber',
        meta: 'Bedroom · deep sage with arched windows',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/7tb1m4q5_IMG_5020.webp',
        span: 'md:col-span-4',
        clip: 'clip-arch',
    },
    {
        id: 'p07',
        title: 'Foyer Contrast',
        meta: 'Emerald wall · warm neutral vault',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/z77rdva5_IMG_5013.webp',
        span: 'md:col-span-4',
        clip: 'clip-notch-tr',
    },
    {
        id: 'p08',
        title: 'Santa Fe Full Interior and Cabinet Makeover',
        meta: null,
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/pyrdvapv_IMG_1942.webp',
        span: 'md:col-span-8',
        clip: 'clip-notch-bl',
    },
    {
        id: 'p09',
        title: 'Commercial Facade',
        meta: 'Exterior · large-format geometric',
        image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/jgsospev_ef7325111e049c3148ada60a1f35d7f61b9a937e3c74464d1e00a7893a87208a.png',
        span: 'md:col-span-12',
        clip: 'clip-notch-tr',
    },
];

const Card = ({ p, index }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.2 });
    const [hover, setHover] = useState(false);
    return (
        <motion.article
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.1, delay: index * 0.05, ease: [0.7, 0, 0.2, 1] }}
            className={`col-span-12 ${p.span}`}
            data-testid={`project-${p.id}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <div className={`relative overflow-hidden ${p.clip} bg-ink-800`}>
                <div className="aspect-[4/5] md:aspect-[5/4] relative">
                    <FadeImage
                        src={p.image}
                        alt={p.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                            transform: hover ? 'scale(1.05)' : 'scale(1)',
                            transition: 'transform 1200ms cubic-bezier(0.7,0,0.2,1), filter 800ms ease, opacity 700ms ease',
                            filter: hover ? 'brightness(1.05) contrast(1.02)' : 'brightness(0.85)',
                        }}
                    />
                    {/* Spotlight */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background: 'radial-gradient(circle at 60% 40%, rgba(0,0,0,0) 25%, rgba(0,0,0,0.55) 85%)',
                        }}
                    />
                    <div className="absolute top-6 left-6 flex items-center gap-2">
                        <span className="w-4 h-px bg-adobe-500" />
                        <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-bone-200">
                            {p.id.toUpperCase()}
                        </span>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                        <div>
                            {p.meta ? (
                                <div className="eyebrow text-bone-300 mb-1">{p.meta}</div>
                            ) : null}
                            <div className="font-display text-2xl md:text-3xl text-bone-100 leading-tight">
                                {p.title}
                            </div>
                        </div>
                        <div
                            className="hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-200 transition-opacity duration-500"
                            style={{ opacity: hover ? 1 : 0 }}
                        >
                            View project
                            <span className="inline-block w-6 h-px bg-bone-200" />
                        </div>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

export const Gallery = () => {
    return (
        <section
            id="work"
            data-testid="msd-gallery"
            className="relative z-20 bg-ink-900 px-6 md:px-10 pb-24 md:pb-40"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="pt-24 md:pt-32 pb-12 grid grid-cols-12">
                    <div className="col-span-12 md:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-px bg-adobe-500" />
                            <span className="eyebrow">Selected Work — 2019 · 2025</span>
                        </div>
                        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-bone-100">
                            A quiet portfolio.
                        </h2>
                    </div>
                    <div className="hidden md:flex md:col-span-6 items-end justify-end">
                        <div className="max-w-sm text-right font-body text-sm font-light text-bone-400">
                            Every project is a document — colour, light, and hand-craft, held together with restraint.
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-8">
                    {PROJECTS.map((p, i) => <Card p={p} index={i} key={p.id} />)}
                </div>
            </div>
        </section>
    );
};
